import { queryOptions, mutationOptions } from '@tanstack/react-query';
import { portalLocaleExtendedToPortalLocale, type ContactForm, type PortalLocale } from '@worldcoo/monolith/portals';
import {
	getPortalAuthorFn,
	getPortalMetadataFn,
	preregisterDonationFn,
	getPortalCampaignMetadataFn,
	getPortalCampaignsFn,
	getPortalCampaignFn,
	getPaymentDetailsFn,
	processPaymentFn,
	refreshPortalAggregatesCacheFn,
	postContactFormFn,
	getPortalCampaignsAggregatesFn,
	getWorldcooGlobalStatsFn,
	getRecentDonationsFn,
} from './server-functions';
import type { SketchDonationInput, PaymentDetailsInput, ProcessPaymentInput } from '@worldcoo/monolith/payments/shared';
import { useSuspenseQuery } from '@tanstack/react-query';
import { notFound, useSearch } from '@tanstack/react-router';
import { useEphemeralStore } from './app-store';
import type { CategoryKey } from '@worldcoo/monolith/core';
import { useDeferredValue } from 'react';
import { usePortalContext } from './use-portal-context';

/**
 * MARK: getPortalCampaignsAggregates
 */
export const portalCampaignsAggregatesOptions = ({ portalId }: { portalId: string }) =>
	queryOptions({
		queryKey: [portalId, 'campaigns-aggregates'],
		queryFn: () => getPortalCampaignsAggregatesFn({ data: { portalId } }),
	});

export function usePortalCampaignsAggregatesQuery() {
	const { portalId } = usePortalContext();
	const { data } = useSuspenseQuery(portalCampaignsAggregatesOptions({ portalId }));
	return data;
}

/**
 * MARK: getPortalAuthor
 */
export const portalAuthorOptions = ({ portalId, locale }: { portalId: string; locale: PortalLocale }) =>
	queryOptions({
		queryKey: [portalId, locale, 'author'],
		queryFn: () => getPortalAuthorFn({ data: { portalId, locale } }),
	});
export function usePortalAuthorQuery() {
	const { portalId, locale } = usePortalContext();
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		portalAuthorOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

/**
 * MARK: getPortalMetadata
 */
export const portalMetadataOptions = ({ portalId, locale }: { portalId: string; locale: PortalLocale }) =>
	queryOptions({
		queryKey: [portalId, locale, 'metadata'],
		queryFn: () => getPortalMetadataFn({ data: { portalId, locale } }),
	});
export function usePortalMetadataQuery() {
	const { portalId, locale } = usePortalContext();
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		portalMetadataOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

/**
 * MARK: getPortalCampaignMetadata
 */
export const portalCampaignMetadataOptions = ({
	portalId,
	locale,
	campaignId,
}: {
	portalId: string;
	locale: PortalLocale;
	campaignId: string;
}) =>
	queryOptions({
		queryKey: [portalId, locale, 'campaigns', campaignId, 'metadata'],
		queryFn: () => getPortalCampaignMetadataFn({ data: { portalId, campaignId, locale } }),
	});
export function usePortalCampaignMetadataQuery(campaignId: string) {
	const { portalId, locale } = usePortalContext();
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		portalCampaignMetadataOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
			campaignId,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

/**
 * MARK: getPortalCampaigns
 */
export const portalCampaignsOptions = ({
	portalId,
	locale,
	query,
	year,
	state,
	causes,
	city,
}: {
	portalId: string;
	locale: PortalLocale;
	query: string | undefined;
	year: number | undefined;
	state: 'finished' | undefined;
	causes: CategoryKey[] | undefined;
	city: string | undefined;
}) =>
	queryOptions({
		queryKey: [portalId, locale, 'campaigns', query, year, state, causes, city],
		queryFn: () => getPortalCampaignsFn({ data: { portalId, locale, query, year, state, causes, city } }),
	});
export function usePortalCampaignsQuery() {
	const { portalId, locale } = usePortalContext();
	const { lang, city } = useSearch({ from: '__root__' });
	const { q, year, state, causes } = useSearch({ from: '/$portalId/' });
	const deferredFilters = useDeferredValue({
		portalId,
		locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		query: q,
		year,
		state,
		causes,
		city,
	});
	const { data } = useSuspenseQuery(portalCampaignsOptions(deferredFilters));
	if ('notFound' in data) return notFound();
	return data;
}

/**
 * MARK: getPortalCampaign
 */
export const portalCampaignOptions = ({
	portalId,
	locale,
	campaignId,
}: {
	portalId: string;
	locale: PortalLocale;
	campaignId: string;
}) =>
	queryOptions({
		queryKey: [portalId, locale, 'campaigns', campaignId],
		queryFn: () => getPortalCampaignFn({ data: { portalId, locale, campaignId } }),
	});
export function usePortalCampaignQuery(campaignId: string) {
	const { portalId, locale } = usePortalContext();
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		portalCampaignOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
			campaignId,
		}),
	);
	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);
	if ('notFound' in data) return notFound();
	return { data, isPageLoaded: pagesLoaded.includes(`campaign-${campaignId}`) };
}

/**
 * MARK: preregisterDonation
 */
export const preregisterDonationOptions = () =>
	mutationOptions({
		mutationKey: ['preregisterDonation'],
		mutationFn: (input: SketchDonationInput) => preregisterDonationFn({ data: input }),
	});

/**
 * MARK: getPaymentDetails
 */
export const getPaymentDetailsOptions = () =>
	mutationOptions({
		mutationKey: ['getPaymentDetails'],
		mutationFn: (input: PaymentDetailsInput) => getPaymentDetailsFn({ data: input }),
	});
/**
 * MARK: processPayment
 */
export const processPaymentOptions = () =>
	mutationOptions({
		mutationKey: ['processPayment'],
		mutationFn: (input: ProcessPaymentInput) => processPaymentFn({ data: input }),
	});

/**
 * MARK: refreshPortalAggregatesCache
 */
export const refreshPortalAggregatesCacheOptions = () =>
	mutationOptions({
		mutationKey: ['refreshPortalAggregatesCache'],
		mutationFn: (input: { portalId: string }) => refreshPortalAggregatesCacheFn({ data: input }),
	});

/**
 * MARK: postContactForm
 */
export const postContactFormOptions = () =>
	mutationOptions({
		mutationKey: ['postContactForm'],
		mutationFn: (input: ContactForm) => postContactFormFn({ data: input }),
	});

/**
 * MARK: getWorldcooGlobalStats
 */
export const worldcooGlobalStatsOptions = () =>
	queryOptions({
		queryKey: ['worldcoo-global-stats'],
		queryFn: () => getWorldcooGlobalStatsFn(),
		// Stale time of 1 hour since stats don't change frequently
		staleTime: 60 * 60 * 1000,
	});

export function useWorldcooGlobalStatsQuery() {
	const { data } = useSuspenseQuery(worldcooGlobalStatsOptions());
	return data;
}

/**
 * MARK: getRecentDonations (for animated counter)
 *
 * Client passes the current 5-minute window key (e.g. "2026-02-13T14:10").
 * Server returns 6 minutes of donation data (1 min margin), sourced from
 * 1 day before. When the key changes to "2026-02-13T14:15", the old
 * data still covers ~1 min past the boundary while the new batch loads.
 */
export type LiveCounterMode = 'day' | 'realtime';

export const recentDonationsOptions = (windowKey: string, mode: LiveCounterMode = 'day') =>
	queryOptions({
		queryKey: ['worldcoo-recent-donations', mode, windowKey],
		queryFn: () => getRecentDonationsFn({ data: { windowKey, mode } }),
		// Cache for 10 minutes as this value is tied to a specific time window and won't change after that.
		staleTime: 10 * 60 * 1000,
	});

const WINDOW_MS = 5 * 60_000;

/** Returns the current 5-minute window key (e.g. "2026-02-13T14:10") */
export function currentWindowKey() {
	const windowStart = Math.floor(Date.now() / WINDOW_MS) * WINDOW_MS;
	return new Date(windowStart).toISOString().slice(0, 16);
}
